import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Input, MessageBarActions, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { Add20Regular, ArrowClockwise20Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

// models
import { Staff } from '../../../models/Entities/Staff/Staff';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';

// components
import PersonFormComponent from '../PersonForm/Person.Form.Component';

// guía
import { GuideTable, GuideTh, GuideRowActions, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';
import { StatusPill } from '../../../theme/components/StatusPill';

// functions
import * as ComponentFunctions from './Staff.List.Functions';

// styles
import './Staff.List.Styles.css';

const PAGE_SIZES = [10, 20, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Listado del staff (guía): búsqueda y paginado en servidor, grilla con nombre, tipo de persona
 * e identificador, acciones en ⋯ (Ver ficha, Editar, Eliminar con confirmación) y aviso de
 * identificadores rotos con su corrección.
 */
const StaffListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();
  const navigate = useNavigate();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(undefined);
  const [selectedPersonId, setSelectedPersonId] = useState<number | undefined>(undefined);
  const [deleting, setDeleting] = useState<Staff | undefined>(undefined);
  const [deleteRequested, setDeleteRequested] = useState(false);

  const [searchText, setSearchText] = useState<string>('');
  const [appliedSearchText, setAppliedSearchText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(ComponentFunctions.DefaultPageSize);

  // La búsqueda la resuelve el servidor: se espera a que la escritura se detenga.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearchText(searchText.trim());
      setPageNumber(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const load = () => props.GetAllStaff({ PageNumber: pageNumber, PageSize: pageSize, SearchText: appliedSearchText || undefined });
  useEffect(() => { load(); }, [pageNumber, pageSize, appliedSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // El diálogo de borrado se cierra solo cuando la operación terminó bien.
  useEffect(() => {
    if (deleteRequested && !props.isDeleting && !props.failOnDeleting) {
      setDeleting(undefined);
      setDeleteRequested(false);
    }
  }, [deleteRequested, props.isDeleting, props.failOnDeleting]);

  // events
  const handleOpenCreate = () => { setSelectedStaffId(undefined); setSelectedPersonId(undefined); openForm(); };
  const handleEdit = (member: Staff) => { setSelectedStaffId(member.id); setSelectedPersonId(member.person?.id); openForm(); };
  const handleDismissForm = () => { setSelectedStaffId(undefined); setSelectedPersonId(undefined); dismissForm(); };
  // La ficha se abre por el id de la PERSONA: adentro están sus datos, teléfonos, correos y domicilios.
  const handleViewDetails = (member: Staff) => { if (member.person?.id) navigate('/staff/' + member.person.id); };

  const isSearching = appliedSearchText.length > 0;
  const showSkeleton = !!props.isLoading && props.staff.length === 0;

  const renderRow = (member: Staff) => {
    const isJuridical = member.person?.personType === PersonTypeEnum.JuridicalPerson;
    const isBroken = ComponentFunctions.HasBrokenIdentifier(member);
    const displayName = ComponentFunctions.GetStaffDisplayName(member);
    const hasPerson = !!member.person?.id;
    return (
      <tr
        key={member.id}
        className={guideRowClassName({ pending: isBroken }, hasPerson ? 'stl-row' : undefined)}
        tabIndex={hasPerson ? 0 : undefined}
        onClick={() => handleViewDetails(member)}
        onKeyDown={(event) => { if (event.key === 'Enter') handleViewDetails(member); }}
        data-testid={`stl-row-${member.id}`}
      >
        <td>
          <div className="stl-name">
            <span className="stl-name__avatar" aria-hidden="true">{ComponentFunctions.GetStaffInitials(member)}</span>
            <div className="stl-name__text">
              <span className="stl-name__title">{displayName}</span>
              <span className="stl-name__sub">{t('staffList.parameters.id', { defaultValue: 'ID' })} {member.id}</span>
            </div>
          </div>
        </td>
        <td>
          <StatusPill tone="neutral" symbol="○">
            {isJuridical ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' }) : t('persons.personType.natural', { defaultValue: 'Persona física' })}
          </StatusPill>
        </td>
        <td className={cell('num')}>
          {isBroken
            ? <StatusPill tone="warning" symbol="!">{t('staffList.identifiers.missing', { defaultValue: 'Sin identificador único' })}</StatusPill>
            : <span className="stl-identifier" title={member.person?.personIdentifier}>{member.person?.personIdentifier}</span>}
        </td>
        <td className={cell('actions')} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
          <GuideRowActions
            label={`${t('staffList.parameters.options', { defaultValue: 'Acciones' })} ${displayName}`}
            actions={[
              { key: 'view', label: t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' }), disabled: !hasPerson, onClick: () => handleViewDetails(member) },
              { key: 'edit', label: t('staffList.parameters.buttons.modifyButton', { defaultValue: 'Modificar' }), disabled: !hasPerson, onClick: () => handleEdit(member) },
              { key: 'delete', label: t('staffList.delete.confirm', { defaultValue: 'Eliminar' }), danger: true, onClick: () => { setDeleteRequested(false); setDeleting(member); } }
            ]}
          />
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    if (showSkeleton) return <GuideSkeleton rows={5} label={t('staffList.loading', { defaultValue: 'Cargando staff...' })} data-testid="stl-loading" />;
    if (props.failOnLoading && props.staff.length === 0) return null;
    if (!props.isLoading && props.staff.length === 0) {
      return isSearching ? (
        <GuideEmptyState
          title={t('staffList.emptySearch', { defaultValue: 'Ningún miembro del staff coincide con la búsqueda' })}
          action={<GuideLinkButton onClick={() => setSearchText('')}>{t('staffList.clearSearch', { defaultValue: 'Limpiar búsqueda' })}</GuideLinkButton>}
          data-testid="stl-no-match"
        />
      ) : (
        <GuideEmptyState
          title={t('staffList.empty', { defaultValue: 'No hay miembros del staff registrados' })}
          text={t('staffList.emptyText', { defaultValue: 'Agregá el primero para cargar sus datos de contacto.' })}
          action={<Button appearance="primary" icon={<Add20Regular />} onClick={handleOpenCreate}>{t('staffList.button.addStaff', { defaultValue: 'Agregar miembro' })}</Button>}
          data-testid="stl-empty"
        />
      );
    }
    return (
      <div className={`stl-table${props.isLoading ? ' is-refreshing' : ''}`} aria-busy={props.isLoading || undefined}>
        <GuideTable
          ariaLabel={t('staffListPage.title', { defaultValue: 'Staff' })}
          data-testid="stl-table"
          footer={
            <GuidePagination
              page={props.pagination.pageNumber}
              pageSize={props.pagination.pageSize || pageSize}
              total={props.pagination.totalRecords}
              onPageChange={setPageNumber}
              pageSizeOptions={PAGE_SIZES}
              onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
            />
          }
        >
          <thead>
            <tr>
              <GuideTh>{t('staffList.parameters.name', { defaultValue: 'Nombre' })}</GuideTh>
              <GuideTh>{t('staffList.parameters.personType', { defaultValue: 'Tipo' })}</GuideTh>
              <GuideTh>{t('staffList.parameters.identifier', { defaultValue: 'Identificador único' })}</GuideTh>
              <GuideTh className={cell('actions')}><span className="stl-sr-only">{t('staffList.parameters.options', { defaultValue: 'Acciones' })}</span></GuideTh>
            </tr>
          </thead>
          <tbody>{props.staff.map(renderRow)}</tbody>
        </GuideTable>
      </div>
    );
  };

  return (
    <div className="stl" data-testid="stl-root">
      <div className="stl-toolbar">
        <Input
          type="search"
          className="stl-search"
          contentBefore={<Search20Regular />}
          placeholder={t('staffList.searchPlaceholder', { defaultValue: 'Buscar miembro del staff' })}
          aria-label={t('staffList.searchPlaceholder', { defaultValue: 'Buscar miembro del staff' })}
          value={searchText}
          maxLength={200}
          onChange={(_event, data) => setSearchText(data.value)}
          data-testid="stl-search"
        />
        <div className="stl-toolbar__counters">
          {props.pagination.totalRecords > 0 && (
            <GuideCountChip label={isSearching ? t('staffList.matching', { defaultValue: 'Coinciden' }) : t('staffList.total', { defaultValue: 'Miembros' })} count={props.pagination.totalRecords} />
          )}
          {props.isLoading && props.staff.length > 0 && <Spinner size="extra-tiny" aria-label={t('staffList.loading', { defaultValue: 'Cargando staff...' })} />}
        </div>
        <div className="stl-toolbar__actions">
          <GuideTooltip content={t('staffList.button.refresh', { defaultValue: 'Actualizar' })} relationship="label">
            <Button appearance="secondary" icon={<ArrowClockwise20Regular />} onClick={load} disabled={!!props.isLoading} data-testid="stl-refresh" />
          </GuideTooltip>
          <Button appearance="primary" icon={<Add20Regular />} onClick={handleOpenCreate} data-testid="stl-add">
            {t('staffList.button.addStaff', { defaultValue: 'Agregar miembro' })}
          </Button>
        </div>
      </div>

      {props.failOnLoading && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="stl-error">
          <MessageBarBody>
            <MessageBarTitle>{t('staffList.errorMessage.error', { defaultValue: 'No fue posible obtener el staff' })}</MessageBarTitle>
            {props.error?.ErrorMessage}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={load}>{t('staffList.retry', { defaultValue: 'Reintentar' })}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {props.hasBrokenIdentifiers && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="stl-broken">
          <MessageBarBody>
            <MessageBarTitle>{t('staffList.identifiers.brokenMessage', { defaultValue: 'Se detectaron personas sin identificador único en esta página' })}</MessageBarTitle>
            {t('staffList.identifiers.brokenSubMessage', { defaultValue: 'Ejecute la corrección de identificadores para regularizarlas' })}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => props.FixPersonIdentifiers()} disabled={!!props.isFixingIdentifiers}>
              {props.isFixingIdentifiers ? t('staffList.identifiers.fixing', { defaultValue: 'Corrigiendo...' }) : t('staffList.identifiers.fix', { defaultValue: 'Corregir identificadores' })}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {renderBody()}

      <PersonFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        staffId={selectedStaffId}
        personId={selectedPersonId}
      />

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.isDeleting) { setDeleting(undefined); setDeleteRequested(false); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('staffList.delete.title', { defaultValue: 'Eliminar miembro del staff' })}</DialogTitle>
            <DialogContent>
              <p className="stl-dialog__text">{t('staffList.delete.description', { defaultValue: '¿Desea eliminar al miembro del staff seleccionado?' })}</p>
              <p className="stl-dialog__name">
                <span className="stl-dialog__label">{t('staffList.delete.element', { defaultValue: 'Miembro' })}:</span> {deleting ? ComponentFunctions.GetStaffDisplayName(deleting) : ''}
              </p>
              {deleteRequested && props.failOnDeleting && (
                <GuideMessageBar intent="error" layout="multiline">
                  <MessageBarBody>
                    <MessageBarTitle>{t('staffList.delete.failed', { defaultValue: 'No se pudo eliminar al miembro.' })}</MessageBarTitle>
                    {props.error?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setDeleting(undefined); setDeleteRequested(false); }} disabled={!!props.isDeleting}>
                {t('staffList.delete.cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button
                appearance="primary"
                className="stl-danger"
                disabled={!!props.isDeleting}
                icon={props.isDeleting ? <Spinner size="extra-tiny" /> : undefined}
                onClick={() => { if (deleting) { setDeleteRequested(true); props.DeleteStaff(deleting.id); } }}
                data-testid="stl-delete-confirm"
              >
                {t('staffList.delete.confirm', { defaultValue: 'Eliminar' })}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(StaffListComponent as any);
